import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { EditUserDialog } from '../components/EditUserDialog';
import { CenterSettingsManager } from '../components/CenterSettingsManager';
import { AdminPacksManager } from '../components/AdminPacksManager';
import { AdminNotificationManager } from '../components/AdminNotificationManager';
import { AdminAccessManager } from '../components/AdminAccessManager';
import { AdminTestimonialsManager } from '../components/AdminTestimonialsManager';
import { TrialAdminManager } from '../components/TrialAdminManager';
import { Users, Trash2, Edit, Search, X, Settings, Upload, Lock, Bell, DollarSign, Package, Quote } from 'lucide-react';
import { usersAPI, authAPI, trialsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function AdminControl() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const { user, updateUserInfo } = useAuth();
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [centerUsers, setCenterUsers] = useState([]);
  const [sessions, setSessions] = useState([]);

  // Load center users and sessions
  useEffect(() => {
    const loadData = async () => {
      if (user?.id) {
        try {
          const users = await usersAPI.getByCenter(user.id);
          setCenterUsers(users || []);
          
          // Load sessions for online status
          const sessionsData = await authAPI.getSessions(user.id);
          setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        } catch (error) {
          console.error('Error loading center users:', error);
        }
      }
    };
    loadData();
    
    // Refresh sessions every 30 seconds for online status
    const interval = setInterval(async () => {
      if (user?.id) {
        try {
          const sessionsData = await authAPI.getSessions(user.id);
          setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        } catch (error) {
          console.error('Error refreshing sessions:', error);
        }
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [user?.id]);

  const activeUsers = centerUsers.filter(u => !u.is_archived);
  const archivedUsers = centerUsers.filter(u => u.is_archived);

  // Helper functions
  const isUserOnline = (userId) => {
    // Check if user has an active session (is_active = true)
    return sessions.some(session => 
      session.user_id === userId && 
      !!session.is_active
    );
  };

  const isPackActive = (finAbonnement) => {
    if (!finAbonnement) return false;
    return new Date(finAbonnement) > new Date();
  };

  // Calculate statistics - only for this center's users
  const usersOnline = activeUsers.filter(u => isUserOnline(u.id)).length;
  const usersOffline = activeUsers.length - usersOnline;
  const packsActive = activeUsers.filter(u => isPackActive(u.fin_abonnement)).length;
  const packsExpired = activeUsers.filter(u => !isPackActive(u.fin_abonnement)).length;
  const totalUsers = centerUsers.length;

  // Filter users based on search query
  const filterUsers = (users) => {
    if (!searchQuery.trim()) return users;
    
    const query = searchQuery.toLowerCase();
    return users.filter(user => {
      const nom = (user.nom || '').toLowerCase();
      const email = (user.email || user.username || '').toLowerCase();
      const telephone = (user.telephone || '').toLowerCase();
      
      return nom.includes(query) || email.includes(query) || telephone.includes(query);
    });
  };

  const filteredActiveUsers = filterUsers(activeUsers);
  const filteredArchivedUsers = filterUsers(archivedUsers);

  const handleArchiveUser = async (userId) => {
    try {
      await usersAPI.archive(userId);
      // Reload users
      const users = await usersAPI.getByCenter(user.id);
      setCenterUsers(users || []);
      toast.success('Utilisateur archivé');
    } catch (error) {
      console.error('Error archiving user:', error);
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleUnarchiveUser = async (userId) => {
    try {
      await usersAPI.restore(userId);
      // Reload users
      const users = await usersAPI.getByCenter(user.id);
      setCenterUsers(users || []);
      toast.success('Utilisateur réactivé');
    } catch (error) {
      console.error('Error unarchiving user:', error);
      toast.error('Erreur lors de la réactivation');
    }
  };

  const handleDeleteUserPermanently = async (userId, userName) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT l'utilisateur "${userName}"? Cette action est irréversible!`)) {
      return;
    }
    
    try {
      const result = await usersAPI.deleteUser(userId);
      if (result.success) {
        toast.success('Utilisateur supprimé définitivement');
        // Reload users
        const users = await usersAPI.getByCenter(user.id);
        setCenterUsers(users || []);
      } else {
        toast.error(result.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveUserEdit = async (userId, updates) => {
    try {
      // Compute credit delta — debit admin if credits increased
      const existingUser = centerUsers.find(u => u.id === userId);
      const oldCredits = existingUser?.credits_ia || 0;
      const newCredits = updates.credits_ia !== undefined ? parseInt(updates.credits_ia) : oldCredits;
      const delta = newCredits - oldCredits;

      if (delta > 0) {
        // Check admin balance first
        const balRes = await trialsAPI.getBalance(user.id);
        const adminBalance = balRes?.essais_restants ?? 0;
        if (adminBalance < delta) {
          toast.error(`Solde insuffisant — vous avez ${adminBalance} essai(s) IA, ${delta} requis pour cette attribution.`);
          return;
        }
        // Debit admin, credit user atomically via distribute
        const distRes = await trialsAPI.distribute(user.id, userId, delta);
        if (!distRes?.success) {
          toast.error(distRes?.message || 'Erreur lors du débit des essais');
          return;
        }
        // Remove credits_ia from updates — distribute already handled it
        const { credits_ia: _, ...otherUpdates } = updates;
        if (Object.keys(otherUpdates).length > 0) {
          await usersAPI.update(userId, otherUpdates);
        }
      } else {
        // No credit increase — update everything directly (reductions are admin's discretion)
        await usersAPI.update(userId, updates);
      }

      const users = await usersAPI.getByCenter(user.id);
      setCenterUsers(users || []);
      setEditingUser(null);
      toast.success('Informations mises à jour');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const tabs = [
    { id: 'settings', label: 'Mon Centre', icon: Settings },
    { id: 'users', label: 'Gestion des utilisateurs', icon: Users },
    { id: 'trials', label: 'Essais IA', icon: Package },
    { id: 'access', label: 'Gestion des accès', icon: Lock },
    { id: 'testimonials', label: 'Témoignages', icon: Quote },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'packs', label: 'Gestion des packs', icon: DollarSign },
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-in">
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight">Panel Administrateur</h1>
          <p className="text-base text-muted-foreground">Gestion de la plateforme TCF Canada</p>
        </div>

        {/* Quick action button */}
        <div className="bg-card p-4 rounded-xl border">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="go-to-exercises"
            className="w-full flex items-center justify-between p-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Upload className="h-6 w-6" />
              <div className="text-left">
                <p className="font-semibold">Accéder aux exercices</p>
                <p className="text-sm opacity-90">Commencer à s'exercer sur les 4 modules</p>
              </div>
            </div>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto">
          <div className="flex min-w-max space-x-2 border-b">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Center Settings Tab */}
        {activeTab === 'settings' && <CenterSettingsManager />}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search bar */}
            <div className="bg-card p-4 rounded-xl border">
              <div className="flex items-center space-x-3">
                <Search className="h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="user-search-input"
                  placeholder="Rechercher par nom, email ou téléphone..."
                  className="flex-1 h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    data-testid="clear-search-button"
                    className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    title="Effacer la recherche"
                  >
                    <X className="h-5 w-5 text-muted-foreground" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-2">
                  {filteredActiveUsers.length + filteredArchivedUsers.length} résultat(s) trouvé(s)
                </p>
              )}
            </div>

            {/* Legend */}
            <div className="bg-card p-4 rounded-xl border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">Légende des indicateurs</h3>
                <div className="text-sm font-medium text-primary">
                  Total utilisateurs inscrits : <span className="text-lg">{totalUsers}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                      <div className="absolute inset-0 h-3 w-3 rounded-full bg-green-500 animate-ping opacity-75" />
                    </div>
                    <span className="text-muted-foreground">Utilisateur en ligne</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-green-500/10 text-green-600 font-medium text-xs">
                    {usersOnline}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-gray-400" />
                    <span className="text-muted-foreground">Utilisateur hors ligne</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-gray-400/10 text-gray-600 font-medium text-xs">
                    {usersOffline}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Pack actif (abonnement valide)</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-600 font-medium text-xs">
                    {packsActive}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-red-500" />
                    <span className="text-muted-foreground">Pack expiré</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-red-500/10 text-red-600 font-medium text-xs">
                    {packsExpired}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-semibold mb-4">Utilisateurs actifs</h2>
              <div className="bg-card rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="active-users-table">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium">Statut</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Nom</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Téléphone</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Pays</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Pack</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Essais IA</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Fin abonnement</th>
                        <th className="px-6 py-4 text-right text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredActiveUsers.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-6 py-8 text-center text-muted-foreground">
                            {searchQuery ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur actif'}
                          </td>
                        </tr>
                      ) : (
                        filteredActiveUsers.map((user) => {
                          const userOnline = isUserOnline(user.id);
                          const packActive = isPackActive(user.fin_abonnement);
                          
                          return (
                            <tr key={user.id} data-testid={`user-row-${user.id}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <div className="relative">
                                  <div 
                                    className={`h-3 w-3 rounded-full ${userOnline ? 'bg-green-500' : 'bg-gray-400'}`}
                                    data-testid={`online-indicator-${user.id}`}
                                    title={userOnline ? 'En ligne' : 'Hors ligne'}
                                  />
                                  {userOnline && (
                                    <div className="absolute inset-0 h-3 w-3 rounded-full bg-green-500 animate-ping opacity-75" />
                                  )}
                                </div>
                                <div 
                                  className={`h-3 w-3 rounded-full ${packActive ? 'bg-blue-500' : 'bg-red-500'}`}
                                  data-testid={`pack-indicator-${user.id}`}
                                  title={packActive ? 'Pack actif' : 'Pack expiré'}
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium">{user.nom || 'Non renseigné'}</td>
                            <td className="px-6 py-4 text-sm">{user.email || user.username}</td>
                            <td className="px-6 py-4 text-sm text-muted-foreground">{user.telephone || '-'}</td>
                            <td className="px-6 py-4 text-sm text-muted-foreground">{user.pays || '-'}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.type_pack === 'Premium' || user.type_pack === 'VIP' ? 'bg-primary/20 text-primary' :
                                user.type_pack === 'Standard' ? 'bg-green-500/20 text-green-600' :
                                'bg-secondary text-secondary-foreground'
                              }`}>
                                {user.type_pack || 'Gratuit'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm">{user.credits_ia}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={packActive ? 'text-foreground' : 'text-destructive font-medium'}>
                                {user.fin_abonnement ? new Date(user.fin_abonnement).toLocaleDateString('fr-FR') : '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => setEditingUser(user)}
                                  data-testid={`edit-user-${user.id}`}
                                  className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleArchiveUser(user.id)}
                                  data-testid={`archive-${user.id}`}
                                  className="px-3 py-1 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground text-sm transition-colors"
                                >
                                  Archiver
                                </button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {archivedUsers.length > 0 && (
              <div>
                <h2 className="text-2xl font-semibold mb-4">Utilisateurs archivés ({archivedUsers.length})</h2>
                <div className="bg-card rounded-xl border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="archived-users-table">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-medium">Nom</th>
                          <th className="px-6 py-4 text-left text-sm font-medium">Email</th>
                          <th className="px-6 py-4 text-left text-sm font-medium">Raison</th>
                          <th className="px-6 py-4 text-right text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredArchivedUsers.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="px-6 py-8 text-center text-muted-foreground">
                              {searchQuery ? 'Aucun utilisateur archivé trouvé' : 'Aucun utilisateur archivé'}
                            </td>
                          </tr>
                        ) : (
                          filteredArchivedUsers.map((user) => (
                            <tr key={user.id} className="bg-red-50/30 dark:bg-red-950/10">
                              <td className="px-6 py-4 text-sm opacity-70">{user.nom || 'Non renseigné'}</td>
                              <td className="px-6 py-4 text-sm opacity-70">{user.email || user.username}</td>
                              <td className="px-6 py-4 text-sm text-red-600 dark:text-red-400">
                                {user.archived_reason || 'Archivé manuellement'}
                              </td>
                              <td className="px-6 py-4 text-right space-x-2">
                                <button
                                  onClick={() => handleUnarchiveUser(user.id)}
                                  data-testid={`unarchive-${user.id}`}
                                  className="px-3 py-1 rounded-lg bg-green-500/10 text-green-600 hover:bg-green-500/20 text-sm transition-colors"
                                >
                                  Réactiver
                                </button>
                                <button
                                  onClick={() => handleDeleteUserPermanently(user.id, user.nom || user.username)}
                                  data-testid={`delete-permanent-${user.id}`}
                                  className="px-3 py-1 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 text-sm transition-colors"
                                >
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Access Manager Tab */}
        {activeTab === 'access' && <AdminAccessManager />}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && <AdminTestimonialsManager />}

        {/* Trials Tab */}
        {activeTab === 'trials' && <TrialAdminManager adminId={user?.id} centerUsers={centerUsers} />}

        {/* Notifications Manager Tab */}
        {activeTab === 'notifications' && <AdminNotificationManager />}

        {/* Packs Manager Tab */}
        {activeTab === 'packs' && <AdminPacksManager />}
      </div>

      {/* Edit User Dialog */}
      {editingUser && (
        <EditUserDialog
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUserEdit}
        />
      )}
    </Layout>
  );
}